import { useMemo } from 'react';
import { fmtDate, fmtDur, post, type ApiErr } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { Block, Cell, Grid, Kpis, Table, type Tone } from '../ui/Block';
import { Head, useAdmin } from '../ui/Shell';
import { confirmSimple } from './dataShared';
import { pl, stale, type Analytics } from './machines';

interface VoiceJob { key: string; target: string; state: string; attempts: number; error: string | null; updatedAt: string | null; nextRunAt: string | null; willNotRetry: boolean; stale: boolean }

const JOB_TONE: Record<string, Tone> = { queued: 'info', running: 'live', done: 'ok', failed: 'bad' };

export function Voices() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const from = useMemo(() => new Date(Date.now() - 86400_000).toISOString(), []);
  const renders = useData<Analytics>(env, `/admin/analytics/tts?from=${encodeURIComponent(from)}`, 0, paused);
  const vj = useData<{ jobs: VoiceJob[] }>(env, '/admin/jobs/voice?limit=50', 15, paused);
  const jobs = vj.data?.jobs ?? [];
  const failed = jobs.filter((j) => j.state === 'failed');
  const retryable = failed.filter((j) => !j.willNotRetry);

  const sums = (renders.data?.rows ?? []).reduce((a, r) => { const [, , lines, speech, synth] = r as [string, string, number, number, number]; return { lines: a.lines + (Number(lines) || 0), speech: a.speech + (Number(speech) || 0), synth: a.synth + (Number(synth) || 0) }; }, { lines: 0, speech: 0, synth: 0 });

  const retry = async () => {
    try {
      const r = await post<{ retried: number }>(env, '/admin/jobs/voice/bulk-retry');
      vj.reload();
      ctx.toast(`Retried ${pl(r.retried, 'voice job')}`, 'Queued again');
    } catch (e) { ctx.toast('Retry failed', (e as ApiErr).message, { logged: false }); }
  };

  return (
    <>
      <Head title="Voices" sub={<>Voice clips and tracks for recordings. Every voice is spoken on a GPU pod's TTS server: see <a href="#" onClick={(e) => { e.preventDefault(); ctx.go('gpu-pool'); }}>GPU pool</a>.</>} />
      <Grid>
        <Cell span={12}>
          <Block title="Renders, last 24 h" sub="From the usage rows; latency is not recorded." asOf={hhmmss(renders.at)} headActs={[{ label: 'Refresh', onClick: renders.reload }]}
            loading={renders.loading && !renders.data} error={renders.error ? { ...describeErr(renders.error, 'The API')!, retry: renders.reload } : null}>
            <Kpis items={[
              { label: 'RENDERS', v: sums.lines.toLocaleString(), sub: 'lines' },
              { label: 'SPEECH', v: fmtDur(sums.speech), sub: 'audio produced' },
              { label: 'SYNTH', v: fmtDur(sums.synth), sub: 'GPU time' },
              { label: 'FAILED', v: failed.length, sub: `${failed.filter((j) => j.willNotRetry).length} will not retry`, fg: failed.length ? 'var(--conf-mid)' : undefined, onClick: () => ctx.go('jobs', { tab: 'voice' }) },
            ]} />
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="voice_jobs" sub="Also listed under Jobs → Voice. The last 50." asOf={hhmmss(vj.at)} stale={stale(vj.ageS, 15)}
            headActs={[...(retryable.length ? [{ label: `Retry ${retryable.length} failed`, onClick: () => confirmSimple(ctx, { title: 'Retry failed voice jobs', app: 'Voices', body: 'Every failed voice job that can retry goes back in the queue.', confirmLabel: 'Retry all', onConfirm: retry }) }] : []), { label: 'Open in Jobs', onClick: () => ctx.go('jobs', { tab: 'voice' }) }]}
            loading={vj.loading && !vj.data} error={vj.error && !vj.data ? { ...describeErr(vj.error, 'The API')!, retry: vj.reload } : null} empty={vj.data && !jobs.length ? 'Queue empty.' : undefined} emptyGood>
            <Table mobile={mobile} minWidth={640} cols={[{ label: 'JOB', width: 'minmax(150px,1.4fr)' }, { label: 'LANG', width: '60px' }, { label: 'STATE', width: 'minmax(100px,.9fr)' }, { label: 'ATTEMPTS', width: '76px', align: 'r' }, { label: 'LAST ERROR', width: 'minmax(140px,1.4fr)' }]}
              rows={jobs.map((j) => {
                const [rec, lang, voice] = j.key.split('/');
                return { key: j.key, onClick: () => ctx.go('jobs', { tab: 'voice', q: rec }), cells: [
                  { t: rec.slice(0, 8), sub: voice || 'default voice', mono: true },
                  { t: lang, mono: true },
                  { pill: { label: j.state.toUpperCase(), tone: JOB_TONE[j.state] ?? 'mute' }, sub: j.willNotRetry ? 'will not retry' : j.updatedAt ? fmtDate(j.updatedAt) : undefined },
                  { t: String(j.attempts), mono: true },
                  { t: j.error ?? '—', fg: j.error ? 'var(--ink-80)' : 'var(--ink-40)' },
                ] };
              })} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
